import {
  Auth0AccountRole,
  MeetingAdminResponse,
  MeetingStatus,
  MeetingsService,
  ParentLimitedResponse,
  StudentLimitedResponse,
} from "client/openapi";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import { Dialog } from "components/Dialog";
import MeetingDialog from "components/MeetingDialog";
import { useEffect, useState } from "react";
import { StepDialogProps, StepFrame } from "./shared";

/** The greeting a first-meeting message starts with. */
export function bookingGreeting(firstName?: string | null): string {
  return `Hi ${firstName || "there"}, I'd like to book you for a meeting.`;
}

/**
 * The First meeting step (students). Done: their first meeting that every
 * tutor accepted, in the meeting window. Not yet: a message to the student
 * and their parents, with a greeting filled in. Nothing is sent until the
 * admin presses Send.
 */
export default function FirstMeetingStepDialog({
  orgId,
  person,
  done,
  onDone,
  onClose,
}: StepDialogProps) {
  const [meeting, setMeeting] = useState<MeetingAdminResponse>();
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!done) return;
    MeetingsService.getMeetingsByStudent({
      studentId: person.id,
      meetingAcceptedByAllTutors: MeetingStatus.ACCEPTED,
    })
      .then((meetings) => {
        // Newest first: the first meeting is the last one.
        const first = meetings[meetings.length - 1];
        if (!first) {
          setMissing(true);
          return undefined;
        }
        return MeetingsService.getMeetingById({ meetingId: first.id }).then(
          (found) => setMeeting(found as MeetingAdminResponse)
        );
      })
      .catch(() => setMissing(true));
  }, [done, person.id]);

  const close = (open: boolean) => !open && onClose();

  if (!done) {
    const student = {
      id: person.id,
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email,
      phone_number: person.phone_number,
      org_id: orgId,
    } as StudentLimitedResponse;
    const parents = person.family
      .filter((member) => member.kind === "parent")
      .map(
        (member) =>
          ({
            id: member.id,
            first_name: member.first_name,
            last_name: member.last_name,
            org_id: orgId,
          } as ParentLimitedResponse)
      );
    return (
      <Dialog open onOpenChange={close}>
        <ComposeMessageDialog
          students={[student]}
          parents={parents.length ? parents : undefined}
          org_id={orgId}
          preloadedContent={bookingGreeting(person.first_name)}
        />
      </Dialog>
    );
  }

  if (meeting) {
    return (
      <Dialog open onOpenChange={close}>
        <MeetingDialog
          role={Auth0AccountRole.ORG_ADMIN}
          event={meeting}
          setEvents={() => {
            onDone();
            return Promise.resolve();
          }}
        />
      </Dialog>
    );
  }

  return (
    <StepFrame title="First meeting" person={person} onClose={onClose}>
      <p className={`text-sm ${missing ? "text-gray-600" : "text-gray-500"}`}>
        {missing
          ? "Their first meeting could not be found. It may have been cancelled."
          : "Loading..."}
      </p>
    </StepFrame>
  );
}
